import React, { useState, forwardRef, useEffect, useRef } from "react";
import { css } from "@emotion/react";

import {
  fonts,
  weights,
  fontSizeLineHeight,
  mediaQueries,
  pxToRem,
  transition,
} from "../utils/css-utils";

import { dropShadows, focusStyles } from "../utils/global-styles";
import { colors } from "../utils/colors";
import { uniqueId } from "../utils/helpers";

type BlogSubNavProps = {
  hasScrolledBelowHeader : boolean
}

const BlogSubNav:React.FC<BlogSubNavProps> = forwardRef(
  ({ hasScrolledBelowHeader, ...props }, ref) => {
    const [isOpen, setisOpen] = useState(false);
    const reglNavId = uniqueId("reglNav_id_");

    const navContainerStyles = css`
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: start;
      position: sticky;
      top: ${hasScrolledBelowHeader ? "0" : pxToRem(55)};
      transition: ${transition()};
      z-index: ${hasScrolledBelowHeader ? 3 : 1};
      background-color: ${colors.black700};
      background-repeat: no-repeat;
      background-size: auto ${pxToRem(48)};
      margin-bottom: ${pxToRem(24)};

      ${mediaQueries.sm} {
        top: ${hasScrolledBelowHeader ? "0" : pxToRem(55)};
      }

      ${mediaQueries.lg} {
        margin-bottom: ${pxToRem(85)};
      }

      ${mediaQueries.xl} {
        top: ${hasScrolledBelowHeader ? "0" : pxToRem(136)};
        transition: ${transition()};
      }

      ${mediaQueries.xxl} {
        top: ${hasScrolledBelowHeader ? "0" : pxToRem(145)};
        flex-direction: row;
        background-size: auto 100%;
        align-items: center;
        justify-content: start;
        height: ${pxToRem(80)};
      }

      main & {
        ${mediaQueries.lg} {
          margin-top: ${pxToRem(-40)};
        }
      }
    `;

    const buttonStyles = css`
      position: relative;
      display: inline-flex;
      align-items: center;
      gap: ${pxToRem(14)};
      width: max-content;
      padding: ${pxToRem(15)} ${pxToRem(48)} ${pxToRem(15)} ${pxToRem(24)};
      border: none;
      text-align: left;
      appearance: none;
      -webkit-appearance: none;
      cursor: pointer;
      background: transparent;

      /* h4 styles */
      font-family: ${fonts.display};
      font-weight: ${weights.black};
      ${fontSizeLineHeight(18, 28)};

      color: ${colors.white};
      ${mediaQueries.md} {
        ${fontSizeLineHeight(20, 32)}
      }

      ${mediaQueries.lg} {
        ${fontSizeLineHeight(24, 36)}
      }

      ${mediaQueries.xxl} {
        display: none;
      }

      &:focus {
        ${focusStyles};
      }
    `;

    const titleStyles = css`
      display: none;
      ${mediaQueries.xxl} {
        display: block;
        margin-left: ${hasScrolledBelowHeader
          ? `${pxToRem(78)}`
          : `${pxToRem(24)}`};
        margin-right: ${pxToRem(77)};
        transition: ${transition()};
      }

      a {
        background: none;
        text-decoration: none;
        white-space: nowrap;

        font-family: ${fonts.display};
        font-weight: ${weights.black};
        ${fontSizeLineHeight(18, 28)};

        color: ${colors.white};
        ${mediaQueries.md} {
          ${fontSizeLineHeight(20, 32)}
        }

        ${mediaQueries.lg} {
          ${fontSizeLineHeight(24, 36)}
        }
      }
    `;

    const searchInputStyles = isSearchOpen => css`
      display: ${isSearchOpen ? `block` : `none`};
      width: 100%;
      border: none;
      position: absolute;
      height: 100%;
      padding: ${pxToRem(24)};
      padding-left: ${hasScrolledBelowHeader
        ? `${pxToRem(78)}`
        : `${pxToRem(24)}`};
      box-shadow: ${dropShadows.medium};
      transition: ${transition()};
      font-family: ${fonts.display};
      font-weight: ${weights.medium};
      background: ${colors.black200};
      ${fontSizeLineHeight(24, 36)};
    `;

    const searchBtn = css`
      background: ${colors.black900};
      border-radius: 10%;
      cursor: pointer;
      border: ${pxToRem(2)} solid transparent;
      width: ${pxToRem(38)};
      height: ${pxToRem(38)};
      z-index: 1;
      position: absolute;
      top: ${pxToRem(11)};
      right: 0;
      margin-right: ${pxToRem(24)};

      ${mediaQueries.md} {
        top: ${pxToRem(13)};
      }

      ${mediaQueries.lg} {
        margin-right: ${hasScrolledBelowHeader
          ? `${pxToRem(81)}`
          : `${pxToRem(24)}`};
      }

      ${mediaQueries.xxl} {
        position: relative;
        top: 0;
        left: 0;
        width: ${pxToRem(52)};
        height: ${pxToRem(52)};
        margin-left: auto;
      }
    `;

    const iconStyles = css`
      circle {
        stroke: ${colors.white};
      }

      path {
        fill: ${colors.white};
      }
    `;

    const [isSearchOpen, setIsSearchOpen] = useState(false);

    const title = "Blog";

    useEffect(() => {
      if (isSearchOpen) {
        const searchInput : HTMLInputElement = inputRef?.current!;
        searchInput?.focus();
        setisOpen(false);
      }
    }, [isSearchOpen]);

    const inputRef = useRef();

    return (
      <div css={navContainerStyles} {...props} ref={ref}>
        <button
          onClick={() => setisOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-controls={reglNavId}
          css={buttonStyles}
        >
          {title}
        </button>
        {/* For larger screens */}
        <div css={titleStyles}>
          <a href="/blog/">{title}</a>
        </div>

        <button
          css={searchBtn}
          onClick={() => {
            setIsSearchOpen(!isSearchOpen);
          }}
          aria-label="search"
        >
          {isSearchOpen ? (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" css={iconStyles}>
              <g clipPath="url(#clip0)">
                <path fillRule="evenodd" clipRule="evenodd" d="M16.364 5.05024L14.9497 3.63603L10 8.58577L5.05025 3.63603L3.63604 5.05024L8.58579 9.99999L3.63604 14.9497L5.05025 16.3639L10 11.4142L14.9497 16.3639L16.364 14.9497L11.4142 9.99999L16.364 5.05024Z" fill="#171719"/>
              </g>
              <defs>
                <clipPath id="clip0">
                  <rect width="20" height="20" fill="white"/>
                </clipPath>
              </defs>
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" css={iconStyles}>
              <path fillRule="evenodd" clipRule="evenodd" d="M16.0666 14.0666L21.2666 19.2666C21.6666 19.8 21.6666 20.6 21.1333 21.1333C20.5999 21.6666 19.7999 21.6666 19.2666 21.1333L14.0666 15.9333C12.7333 17 10.8666 17.6666 8.99992 17.6666C4.19992 17.6666 0.333252 13.8 0.333252 8.99998C0.333252 4.19998 4.19992 0.333313 8.99992 0.333313C13.7999 0.333313 17.6666 4.19998 17.7999 8.99998C17.7999 10.8666 17.1333 12.6 16.0666 14.0666ZM2.99992 8.99998C2.99992 12.3333 5.66658 15 8.99992 15C12.3333 15 14.9999 12.3333 14.9999 8.99998C14.9999 5.66665 12.3333 2.99998 8.99992 2.99998C5.66658 2.99998 2.99992 5.66665 2.99992 8.99998Z" fill="#171719"/>
            </svg>
          )}
        </button>

        <input
          css={searchInputStyles(isSearchOpen)}
          aria-hidden={!isSearchOpen}
          placeholder="Search"
          onKeyDown={e => {
            if (e.key.match(/enter/i)) {
              window.location.href = `/blog/search/?search=${e.target.value}`;
            }
          }}
          ref={inputRef}
        />
      </div>
    );
  }
);

BlogSubNav.displayName = "BlogSubNav";

export default BlogSubNav;
